import * as Units from '../../Units';
import {isRainMetric} from '../../Generic/RainMetricUtils';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {getMetricPrecision} from '../formatters';
import {getYearColor, ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';
export interface AnnualComparisonChartOptions {
  /** Array of datasets, one per year. Each dataset has category-based data. */
  yearDatasets: Array<{
    year: number;
    data: number[];
  }>;
  /** Month category labels (e.g. ['Jan', 'Feb', ...]) */
  categories: string[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  metric: string;
  height?: number;
}

/**
 * Multi-year comparison chart with monthly categories.
 * Each year is a separate series, overlaid on the same x-axis.
 */
export const annualComparisonChart = (
  options: AnnualComparisonChartOptions,
): ChartConfig => {
  const {
    yearDatasets,
    categories,
    theme,
    timezone,
    units,
    metric,
    height = 400,
  } = options;

  const isBarChart =
    isRainMetric(metric) || metric === 'LIGHTNING_STRIKE_COUNT';
  const precision = getMetricPrecision(metric);

  // Build merged data array with category-based x-axis
  const data: ChartDataPoint[] = categories.map((cat, index) => {
    const point: ChartDataPoint = {timestamp: index, category: cat};
    for (const ds of yearDatasets) {
      point[`Y_${ds.year}`] = ds.data[index] ?? null;
    }
    return point;
  });

  const series: SeriesConfig[] = yearDatasets.map((ds, index) => ({
    id: `year-${ds.year}`,
    type: isBarChart ? ('bar' as const) : ('line' as const),
    dataKey: `Y_${ds.year}`,
    name: String(ds.year),
    color: getYearColor(index, theme.isDark),
    yAxisIndex: 0,
    interpolation: 'monotone' as const,
    lineWidth: 2,
    showMarkers: false,
    zIndex: yearDatasets.length - index,
    barRadius: isBarChart
      ? [theme.borderRadius, theme.borderRadius, 0, 0]
      : undefined,
  }));

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      type: 'category',
      timezone,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: metric,
        metric,
        units,
        theme,
        precision,
      }),
    ],
    tooltip: {
      ...createTooltipConfig({
        timezone,
        granularity: 'M',
        metric,
        units,
        theme,
      }),
      sortByValue: true,
    },
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

export interface RainComparisonChartOptions {
  /** Array of year datasets with cumulative monthly rain totals */
  yearDatasets: Array<{
    year: number;
    data: number[];
  }>;
  categories: string[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
  /** Rain metric key (e.g. 'RR_1H', 'RR_3H', etc.). Defaults to 'RR_1H'. */
  metric?: string;
}

/**
 * Year-to-date cumulative rainfall comparison across years.
 */
export const rainComparisonChart = (
  options: RainComparisonChartOptions,
): ChartConfig => {
  const {
    yearDatasets,
    categories,
    theme,
    timezone,
    units,
    height = 400,
    metric = 'RR_1H',
  } = options;

  const data: ChartDataPoint[] = categories.map((cat, index) => {
    const point: ChartDataPoint = {timestamp: index, category: cat};
    for (const ds of yearDatasets) {
      point[`Y_${ds.year}`] = ds.data[index] ?? null;
    }
    return point;
  });

  const series: SeriesConfig[] = yearDatasets.map((ds, index) => ({
    id: `rain-${ds.year}`,
    type: 'line' as const,
    dataKey: `Y_${ds.year}`,
    name: String(ds.year),
    color: getYearColor(index, theme.isDark),
    yAxisIndex: 0,
    interpolation: 'monotone' as const,
    lineWidth: 2,
    showMarkers: false,
    zIndex: yearDatasets.length - index,
  }));

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      type: 'category',
      timezone,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'rain',
        metric,
        units,
        theme,
        domain: [0, 'auto'],
      }),
    ],
    tooltip: {
      ...createTooltipConfig({
        timezone,
        granularity: 'M',
        metric,
        units,
        theme,
      }),
      sortByValue: true,
    },
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

export interface TemperatureComparisonChartOptions {
  /** Array of year datasets with year-to-date average temperatures */
  yearDatasets: Array<{
    year: number;
    data: number[];
  }>;
  categories: string[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
}

/**
 * Year-to-date average temperature comparison across years.
 */
export const temperatureComparisonChart = (
  options: TemperatureComparisonChartOptions,
): ChartConfig => {
  const {
    yearDatasets,
    categories,
    theme,
    timezone,
    units,
    height = 400,
  } = options;

  const data: ChartDataPoint[] = categories.map((cat, index) => {
    const point: ChartDataPoint = {timestamp: index, category: cat};
    for (const ds of yearDatasets) {
      point[`Y_${ds.year}`] = ds.data[index] ?? null;
    }
    return point;
  });

  const series: SeriesConfig[] = yearDatasets.map((ds, index) => ({
    id: `temp-${ds.year}`,
    type: 'line' as const,
    dataKey: `Y_${ds.year}`,
    name: String(ds.year),
    color: getYearColor(index, theme.isDark),
    yAxisIndex: 0,
    interpolation: 'monotone' as const,
    lineWidth: 2,
    showMarkers: false,
    zIndex: yearDatasets.length - index,
  }));

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      type: 'category',
      timezone,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'temperature',
        metric: 'T',
        units,
        theme,
        precision: 1,
      }),
    ],
    tooltip: {
      ...createTooltipConfig({
        timezone,
        granularity: 'M',
        metric: 'T',
        units,
        theme,
      }),
      sortByValue: true,
    },
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
